/**
 * Image Compression & Conversion Service for AI Vision APIs
 * 
 * Pipeline:
 * 1. Format Conversion: AVIF/HEIC/BMP/TIFF → PNG (via Canvas API)
 *    PNG/JPEG/WebP/GIF → unchanged (already API-compatible)
 * 2. Resize + JPEG Compression: max 1024px longest side, JPEG 85% quality
 * 
 * Result: ~200-300KB JPEG instead of 2-3MB originals
 * Saves tokens, API costs, and network bandwidth.
 * 
 * Shopify-supported formats that need conversion:
 * - AVIF, HEIC, BMP, TIFF, SVG → converted to PNG first
 * - PNG, JPEG, WebP, GIF → passed through directly
 */

// ============================================================
// Step 1: Format Conversion
// ============================================================

/**
 * MIME types that OpenAI and Gemini Vision APIs directly accept
 */
const API_COMPATIBLE_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
]);

/**
 * Check if a MIME type is API-compatible
 * @param {string} mimeType
 * @returns {boolean}
 */
export const isApiCompatibleFormat = (mimeType) => {
  return API_COMPATIBLE_MIME_TYPES.has((mimeType || '').toLowerCase());
};

/**
 * Detect MIME type from a data URL or file extension
 * @param {string} urlOrDataUrl - The URL or data URL
 * @returns {string} Detected MIME type
 */
const detectMimeType = (urlOrDataUrl) => {
  if (!urlOrDataUrl) return 'image/jpeg';
  
  // Data URL
  if (urlOrDataUrl.startsWith('data:')) {
    const match = urlOrDataUrl.match(/data:([^;]+)/);
    return match ? match[1] : 'image/jpeg';
  }
  
  // File URL - detect from extension
  const url = urlOrDataUrl.split('?')[0].toLowerCase();
  if (url.endsWith('.png')) return 'image/png';
  if (url.endsWith('.jpg') || url.endsWith('.jpeg')) return 'image/jpeg';
  if (url.endsWith('.webp')) return 'image/webp';
  if (url.endsWith('.gif')) return 'image/gif';
  if (url.endsWith('.avif')) return 'image/avif';
  if (url.endsWith('.heic') || url.endsWith('.heif')) return 'image/heic';
  if (url.endsWith('.bmp')) return 'image/bmp';
  if (url.endsWith('.tiff') || url.endsWith('.tif')) return 'image/tiff';
  if (url.endsWith('.svg')) return 'image/svg+xml';
  
  // Default
  return 'image/jpeg';
};

/**
 * Convert a File with unsupported format to PNG via Canvas API.
 * If the format is already compatible, returns the File unchanged.
 * @param {File} file - The image file to convert
 * @returns {Promise<File>} - The converted (or original) file
 */
export const convertToCompatibleFormat = (file) => {
  if (isApiCompatibleFormat(file.type)) {
    return Promise.resolve(file);
  }

  console.log(`🔄 Converting "${file.type}" → image/png`);

  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(objectUrl);
          return reject(new Error('Could not get 2D canvas context'));
        }

        ctx.drawImage(img, 0, 0);

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            if (!blob) return reject(new Error('Canvas toBlob returned null'));

            const baseName = file.name.replace(/\.[^.]+$/, '') || 'converted';
            const convertedFile = new File([blob], `${baseName}.png`, {
              type: 'image/png',
              lastModified: Date.now(),
            });

            console.log(
              `✅ Converted: ${file.name} (${file.type}, ${Math.round(file.size / 1024)} KB)`
              + ` → ${convertedFile.name} (image/png, ${Math.round(convertedFile.size / 1024)} KB)`
            );

            resolve(convertedFile);
          },
          'image/png'
        );
      } catch (err) {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(`Conversion failed: ${err}`));
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Cannot load format "${file.type}". Please use PNG, JPEG, or WebP.`));
    };

    img.src = objectUrl;
  });
};


// ============================================================
// Step 2: Resize + JPEG Compression
// ============================================================

/**
 * Resize a base64-encoded image to a max dimension and optionally force JPEG output.
 * 
 * @param {string} base64 - Base64 string WITHOUT data:...;base64, prefix
 * @param {string} mimeType - Original MIME type (e.g. 'image/png')
 * @param {number} maxSize - Max width OR height in pixels (default: 1024)
 * @param {boolean} forceJpeg - If true, output is always JPEG (real compression even for PNG input)
 * @returns {Promise<{base64: string, mimeType: string}>} - Compressed image data
 */
export const resizeImageBase64 = (
  base64,
  mimeType,
  maxSize = 1024,
  forceJpeg = true
) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = `data:${mimeType};base64,${base64}`;

    img.onload = () => {
      let width = img.width;
      let height = img.height;

      // Only resize if larger than maxSize
      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = Math.round(height * (maxSize / width));
          width = maxSize;
        } else {
          width = Math.round(width * (maxSize / height));
          height = maxSize;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Could not get 2D canvas context'));

      ctx.drawImage(img, 0, 0, width, height);

      // JPEG forces real compression (PNG ignores the quality parameter!)
      const outputMimeType = forceJpeg ? 'image/jpeg' : mimeType;
      const quality = forceJpeg ? 0.85 : 0.9;

      const resizedDataUrl = canvas.toDataURL(outputMimeType, quality);
      const resizedBase64 = resizedDataUrl.split(',')[1];

      console.log(
        `📐 Resized: ${img.width}x${img.height} → ${width}x${height}`
        + (forceJpeg ? ' (forced JPEG 85%)' : '')
      );
      console.log(
        `📦 Size: ${Math.round(base64.length * 0.75 / 1024)} KB → ${Math.round(resizedBase64.length * 0.75 / 1024)} KB`
        + ` (${Math.round((1 - resizedBase64.length / base64.length) * 100)}% smaller)`
      );

      resolve({
        base64: resizedBase64,
        mimeType: outputMimeType
      });
    };

    img.onerror = () => reject(new Error('Failed to load image for resizing'));
  });
};


// ============================================================
// High-Level API: Compress Image URLs for AI Analysis
// ============================================================

/**
 * Compress a Shopify CDN image URL for AI analysis.
 * Downloads the image, resizes to maxSize, and converts to JPEG.
 * Returns a data: URL ready for vision API consumption.
 * 
 * @param {string} imageUrl - The original image URL (Shopify CDN, data: URL, etc.)
 * @param {number} maxSize - Max dimension in pixels (default: 1024)
 * @returns {Promise<string>} - Compressed data: URL (always image/jpeg)
 */
export const compressImageForAnalysis = async (imageUrl, maxSize = 1024) => {
  try {
    // If it's already a data: URL, extract base64 and compress
    if (imageUrl.startsWith('data:')) {
      const mimeType = detectMimeType(imageUrl);
      const base64Data = imageUrl.split(',')[1];
      
      if (!base64Data) {
        console.warn('⚠️ Invalid data URL, returning original');
        return imageUrl;
      }
      
      const result = await resizeImageBase64(base64Data, mimeType, maxSize, true);
      return `data:${result.mimeType};base64,${result.base64}`;
    }
    
    // For regular URLs (Shopify CDN, etc.), fetch and compress
    console.log(`🔄 Fetching image for compression: ${imageUrl.substring(0, 80)}...`);
    
    const response = await fetch(imageUrl);
    if (!response.ok) {
      console.warn(`⚠️ Failed to fetch image (${response.status}), returning original URL`);
      return imageUrl;
    }
    
    const blob = await response.blob();
    const mimeType = blob.type || detectMimeType(imageUrl);
    
    // Convert blob to base64
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = () => reject(new Error('Failed to read image blob'));
      reader.readAsDataURL(blob);
    });
    
    const originalSizeKB = Math.round(base64.length * 0.75 / 1024);
    console.log(`📥 Fetched image: ${originalSizeKB} KB (${mimeType})`);
    
    // Resize and compress to JPEG
    const result = await resizeImageBase64(base64, mimeType, maxSize, true);
    const compressedSizeKB = Math.round(result.base64.length * 0.75 / 1024);
    
    console.log(
      `✅ Compressed for analysis: ${originalSizeKB} KB → ${compressedSizeKB} KB`
      + ` (${Math.round((1 - compressedSizeKB / originalSizeKB) * 100)}% savings)`
    );
    
    return `data:${result.mimeType};base64,${result.base64}`;
  } catch (error) {
    console.error('❌ Image compression failed, returning original URL:', error.message);
    // Fallback: return original URL if compression fails
    return imageUrl;
  }
};

/**
 * Compress multiple image URLs for AI analysis in parallel.
 * Logs total compression savings.
 * 
 * @param {string[]} imageUrls - Array of image URLs
 * @param {number} maxSize - Max dimension in pixels (default: 1024)
 * @returns {Promise<string[]>} - Array of compressed data: URLs
 */
export const compressImagesForAnalysis = async (imageUrls, maxSize = 1024) => {
  if (!imageUrls || imageUrls.length === 0) return [];
  
  console.log(`🖼️ Compressing ${imageUrls.length} image(s) for AI analysis (max ${maxSize}px)...`);
  const startTime = Date.now();
  
  const compressedUrls = await Promise.all(
    imageUrls.map(url => compressImageForAnalysis(url, maxSize))
  );
  
  const elapsed = Date.now() - startTime;
  console.log(`✅ All ${imageUrls.length} image(s) compressed in ${elapsed}ms`);
  
  return compressedUrls;
};

/**
 * Extract the base64 data and mime type from a compressed data URL.
 * Useful when providers need separate base64 + mimeType (e.g. Gemini inlineData).
 * 
 * @param {string} dataUrl - A data: URL
 * @returns {{base64: string, mimeType: string}} - The extracted data
 */
export const extractBase64FromDataUrl = (dataUrl) => {
  if (!dataUrl || !dataUrl.startsWith('data:')) {
    return { base64: '', mimeType: 'image/jpeg' };
  }
  
  const [header, base64Data] = dataUrl.split(',');
  const mimeMatch = header.match(/data:([^;]+)/);
  
  return {
    base64: base64Data || '',
    mimeType: mimeMatch ? mimeMatch[1] : 'image/jpeg'
  };
};
